<template>
  <div>
    <div class="page-card">
      <h2 class="page-title">菜谱管理</h2>
      <el-form label-width="110px" @submit.native.prevent="saveDish">
        <el-row :gutter="20">
          <el-col :xs="24" :sm="12" :md="8">
            <el-form-item label="菜品名称">
              <el-input v-model.trim="form.name" placeholder="请输入" clearable />
            </el-form-item>
          </el-col>
          <el-col :xs="24" :sm="12" :md="8">
            <el-form-item label="分类">
              <el-select v-model="form.category" placeholder="请选择" clearable style="width: 100%;">
                <el-option
                  v-for="item in categories"
                  :key="item.value"
                  :label="item.label"
                  :value="item.value"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :xs="24" :sm="12" :md="8">
            <el-form-item label="单人份售价">
              <el-input v-model="form.price" placeholder="请输入" type="number" />
            </el-form-item>
          </el-col>
          <el-col :xs="24" :sm="12" :md="8">
            <el-form-item label="单人份成本">
              <el-input v-model="form.cost" placeholder="请输入" type="number" />
            </el-form-item>
          </el-col>
        </el-row>
        <div class="form-actions">
          <el-button type="primary" native-type="submit">
            {{ editingId ? '保存修改' : '新增菜品' }}
          </el-button>
          <el-button native-type="button" @click="triggerImport">导入菜单</el-button>
          <el-button v-if="editingId" native-type="button" @click="resetForm">取消</el-button>
        </div>
      </el-form>
      <input
        ref="importInput"
        type="file"
        accept=".xlsx,.xls,.csv"
        style="display: none;"
        @change="onImportFile"
      >
    </div>

    <div class="page-card">
      <div class="recipe-list-head">
        <h2 class="page-title">已录入菜品</h2>
        <div class="recipe-list-toolbar">
          <el-button size="small" @click="exportCurrentList">导出菜谱</el-button>
          <el-button size="small" @click="downloadTemplate">下载模板</el-button>
          <el-cascader
            v-model="filterPath"
            :options="dishCascaderOptions"
            :props="cascaderProps"
            filterable
            clearable
            placeholder="搜索菜名或按分类筛选"
            style="width: 280px;"
          />
          <el-button
            v-if="selectedDishes.length"
            type="primary"
            size="small"
            @click="goGenerateMenu"
          >生成菜单</el-button>
        </div>
      </div>
      <el-table
        ref="dishTable"
        :data="pagedDishes"
        border
        stripe
        highlight-current-row
        row-key="id"
        empty-text="暂无菜品，请先录入"
        style="width: 100%;"
        @selection-change="onSelectionChange"
      >
        <el-table-column type="selection" width="48" align="center" reserve-selection />
        <el-table-column prop="name" label="菜品名称" min-width="140" />
        <el-table-column label="分类" width="120" align="center">
          <template slot-scope="scope">{{ categoryLabel(scope.row.category) }}</template>
        </el-table-column>
        <el-table-column label="单人份售价" width="130" align="center">
          <template slot-scope="scope">{{ formatMoney(scope.row.price) }}</template>
        </el-table-column>
        <el-table-column label="单人份成本" width="130" align="center">
          <template slot-scope="scope">{{ formatMoney(scope.row.cost) }}</template>
        </el-table-column>
        <el-table-column label="操作" width="140" align="center">
          <template slot-scope="scope">
            <el-button type="text" @click="startEdit(scope.row)">编辑</el-button>
            <el-button type="text" @click="removeDish(scope.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
      <div v-if="filteredDishes.length" class="recipe-pagination">
        <el-pagination
          background
          layout="total, prev, pager, next"
          :page-size="pageSize"
          :current-page.sync="currentPage"
          :total="filteredDishes.length"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { CATEGORIES, categoryLabel, isValidCategory } from '@/constants/categories'
import { loadDishes, saveDishes, createDishId } from '@/utils/storage'
import { downloadDishTemplate, exportDishes, parseDishFile } from '@/utils/dishExcel'
import { DISH_CASCADER_PROPS, buildDishCascaderOptions } from '@/utils/dishCascader'
import { setPendingMenu } from '@/utils/pendingMenu'

export default {
  name: 'RecipeManage',
  data() {
    return {
      categories: CATEGORIES,
      dishes: [],
      filterPath: [],
      selectedDishes: [],
      cascaderProps: DISH_CASCADER_PROPS,
      currentPage: 1,
      pageSize: 10,
      editingId: '',
      form: {
        name: '',
        category: '',
        price: '',
        cost: ''
      }
    }
  },
  computed: {
    filteredDishes() {
      const path = this.filterPath || []
      if (!path.length) return this.dishes
      if (path.length === 1) {
        return this.dishes.filter((d) => d.category === path[0])
      }
      return this.dishes.filter((d) => d.id === path[1])
    },
    pagedDishes() {
      const start = (this.currentPage - 1) * this.pageSize
      return this.filteredDishes.slice(start, start + this.pageSize)
    },
    dishCascaderOptions() {
      return buildDishCascaderOptions(this.dishes)
    }
  },
  watch: {
    filterPath() {
      this.currentPage = 1
    },
    filteredDishes() {
      this.clampPage()
    }
  },
  created() {
    this.dishes = loadDishes()
    this.persist()
  },
  methods: {
    categoryLabel,
    formatMoney(n) {
      return Number(n).toFixed(2)
    },
    persist() {
      saveDishes(this.dishes)
    },
    clampPage() {
      const maxPage = Math.max(1, Math.ceil(this.filteredDishes.length / this.pageSize) || 1)
      if (this.currentPage > maxPage) this.currentPage = maxPage
    },
    onSelectionChange(rows) {
      this.selectedDishes = rows || []
    },
    goGenerateMenu() {
      const table = this.$refs.dishTable
      const rows = (table && table.selection && table.selection.length)
        ? table.selection
        : this.selectedDishes
      if (!rows.length) {
        this.$message.warning('请先勾选菜品')
        return
      }
      setPendingMenu(rows)
      this.$router.push({ path: '/generate' })
    },
    resetForm() {
      this.editingId = ''
      this.form = { name: '', category: '', price: '', cost: '' }
    },
    validate() {
      if (!this.form.name) {
        this.$message.warning('请填写菜品名称')
        return null
      }
      if (!isValidCategory(this.form.category)) {
        this.$message.warning('请选择菜品分类')
        return null
      }
      const price = Number(this.form.price)
      const cost = Number(this.form.cost)
      if (!Number.isFinite(price) || price < 0) {
        this.$message.warning('单人份售价须为大于等于 0 的数字')
        return null
      }
      if (!Number.isFinite(cost) || cost < 0) {
        this.$message.warning('单人份成本须为大于等于 0 的数字')
        return null
      }
      if (cost > price) {
        this.$message.info('提示：当前单人份成本高于单人份售价')
      }
      return {
        name: this.form.name,
        category: this.form.category,
        price,
        cost
      }
    },
    saveDish() {
      const payload = this.validate()
      if (!payload) return

      if (this.editingId) {
        const idx = this.dishes.findIndex((d) => d.id === this.editingId)
        if (idx >= 0) {
          this.$set(this.dishes, idx, { id: this.editingId, ...payload })
        }
        this.$message.success('已保存修改')
      } else {
        this.dishes.push({ id: createDishId(), ...payload })
        this.$message.success('已新增菜品')
        this.$nextTick(() => {
          this.currentPage = Math.max(1, Math.ceil(this.filteredDishes.length / this.pageSize) || 1)
        })
      }
      this.persist()
      this.resetForm()
    },
    startEdit(dish) {
      this.editingId = dish.id
      this.form = {
        name: dish.name,
        category: dish.category,
        price: dish.price,
        cost: dish.cost
      }
    },
    removeDish(dish) {
      this.$confirm(`确认删除「${dish.name}」？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.dishes = this.dishes.filter((d) => d.id !== dish.id)
        this.persist()
        if (this.editingId === dish.id) this.resetForm()
        this.$message.success('已删除')
      }).catch(() => {})
    },
    downloadTemplate() {
      downloadDishTemplate()
    },
    exportCurrentList() {
      if (!this.filteredDishes.length) {
        this.$message.warning('当前没有可导出的菜品')
        return
      }
      exportDishes(this.filteredDishes)
    },
    triggerImport() {
      this.$refs.importInput.value = ''
      this.$refs.importInput.click()
    },
    async onImportFile(event) {
      const file = event.target.files && event.target.files[0]
      if (!file) return
      try {
        const { dishes, skipped } = await parseDishFile(file)
        if (!dishes.length) {
          this.$message.warning(skipped ? '没有可导入的有效行，请检查分类和价格' : '文件中没有数据')
          return
        }
        dishes.forEach((item) => {
          this.dishes.push({ id: createDishId(), ...item })
        })
        this.persist()
        const extra = skipped ? `，跳过 ${skipped} 行` : ''
        this.$message.success(`成功导入 ${dishes.length} 道菜${extra}`)
        this.currentPage = Math.max(1, Math.ceil(this.filteredDishes.length / this.pageSize) || 1)
      } catch (err) {
        this.$message.error(err.message || '导入失败')
      }
    }
  }
}
</script>

<style scoped>
.recipe-list-head {
  margin-bottom: 12px;
}

.recipe-list-head .page-title {
  margin-bottom: 12px;
}

.recipe-list-toolbar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.recipe-pagination {
  margin-top: 16px;
  text-align: right;
}
</style>
